import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_FEATURE_CODES, FEATURE_CODE_ACTIONS, FEATURE_CODE_CATALOGUE, featureCodeClashes,
  type FeatureCodeAction,
} from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { tmsKeys, useFeatureCodes } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';

type Codes = Record<FeatureCodeAction, string>;

/**
 * Feature codes: what this account's phones dial to change something themselves.
 *
 * Every one is the account's to choose (docs/26 E1): somebody moving from
 * another system keeps the codes their people already dial. One table for all
 * of them, because "what do our phones dial?" is one question, and the rules
 * that stop two codes colliding are the API's own (packages/contracts),
 * checked here as the codes are typed.
 */
export function FeatureCodesTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const writable = can('featurecode:write');
  const current = useFeatureCodes(tenantId);
  const [draft, setDraft] = useState<Codes>(() => ({ ...DEFAULT_FEATURE_CODES }));

  // Seeded from what is saved, and again after every save, so the fields never
  // show a code the switch is not actually using.
  useEffect(() => {
    if (current.data) setDraft({ ...current.data.codes });
  }, [current.data]);

  const save = useMutation({
    mutationFn: (body: Codes) => api.put(`/v1/tenants/${tenantId}/feature-codes`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: tmsKeys.featureCodes(tenantId) }),
  });

  const trimmed = useMemo(
    () => Object.fromEntries(FEATURE_CODE_ACTIONS.map((a) => [a, draft[a].trim()])) as Codes,
    [draft]);
  const clashes = featureCodeClashes(trimmed);
  const saved = current.data?.codes;
  const changed = !!saved && FEATURE_CODE_ACTIONS.some((a) => trimmed[a] !== saved[a]);
  const onDefaults = FEATURE_CODE_ACTIONS.every((a) => trimmed[a] === DEFAULT_FEATURE_CODES[a]);
  const groups = [...new Set(FEATURE_CODE_CATALOGUE.map((c) => c.group))];

  if (current.error) return <ErrorBanner error={current.error} />;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Feature codes</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            What this account's phones dial to change something themselves.
          </p>
        </div>
        {writable && (
          <div className="arc-row" style={{ gap: 8 }}>
            <button type="button" className="arc-btn" disabled={save.isPending || onDefaults}
              onClick={() => setDraft({ ...DEFAULT_FEATURE_CODES })}>
              Use the defaults
            </button>
            <button type="button" className="arc-btn arc-btn-primary"
              disabled={save.isPending || !changed || Object.keys(clashes).length > 0}
              onClick={() => save.mutate(trimmed)}>
              {save.isPending ? 'Saving…' : 'Save codes'}
            </button>
          </div>
        )}
      </div>

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Feature</th><th>Does</th><th style={{ width: 170 }}>Code</th><th>Dialled as</th>
              </tr>
            </thead>
            {!current.data ? <TableSkeleton rows={10} cols={4} /> : (
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g}>
                    {FEATURE_CODE_CATALOGUE.filter((c) => c.group === g).map((c, i) => {
                      const problem = clashes[c.action];
                      const code = trimmed[c.action];
                      return (
                        <tr key={c.action}>
                          <td style={{ fontWeight: 550 }}>{i === 0 ? g : ''}</td>
                          <td>{c.label}</td>
                          <td>
                            <input className="arc-input arc-mono" value={draft[c.action]} inputMode="tel"
                              aria-label={`${g}: ${c.label}`} aria-invalid={problem !== undefined}
                              disabled={!writable || save.isPending}
                              onChange={(e) => setDraft((d) => ({ ...d, [c.action]: e.target.value }))} />
                            {problem && (
                              <div className="arc-error" style={{ fontSize: 11.5, marginTop: 3 }}>{problem}</div>
                            )}
                          </td>
                          <td className="arc-muted">
                            <span className="arc-mono">{code}</span>
                            {c.then && <span className="arc-subtle"> then {c.then}</span>}
                            {code !== DEFAULT_FEATURE_CODES[c.action] && (
                              <> <Badge tone="info">default {DEFAULT_FEATURE_CODES[c.action]}</Badge></>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>

      <p className="arc-subtle" style={{ fontSize: 11.5, marginTop: 10 }}>
        A change reaches the phones within about a minute. The test numbers 9196 (echo) and
        9195 (tone) are not feature codes and stay as they are.
      </p>
    </>
  );
}
